import { Component, OnInit } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { CustProdAccBcsGet, CustProdAccBcsGridRow, CustProdAccBcsService } from '@app/services/cust-prod-acc-bcs.service';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { AccountItem, TypeaheadService } from '@app/services/typeahead.service';

@Component({
    selector: 'tt-cust-prod-acc-bcs',
    templateUrl: './cust-prod-acc-bcs.component.html',
    styleUrls: ['./cust-prod-acc-bcs.component.css'],
    standalone: false
})
export class CustProdAccBcsComponent implements OnInit {
    public setting?: CustProdAccBcsGet;

    public selectListCompanies: BusinesscoItem[] = [];

    public grid?: GridOptions<CustProdAccBcsGridRow>;

    constructor(
        private modal: ModalService,
        private custProdAccBcs: CustProdAccBcsService,
        private logistic: LogisticService,
        private typeahead: TypeaheadService
    ) {}

    public searchAccountName = (params?: { value?: string }): Promise<AccountItem[]> => {
        return this.typeahead.lookUpSearchAccounts({ searchtext: params?.value ?? '', businessco_no: this.setting?.businessco_no ?? '' });
    };

    public searchAccountNameFree = (params?: { value?: string }): Promise<AccountItem[]> => {
        return this.searchAccounts(params);
    };

    public searchAccountNameExport = (params?: { value?: string }): Promise<AccountItem[]> => {
        return this.searchAccounts(params);
    };

    public searchAccountNameFreeExport = (params?: { value?: string }): Promise<AccountItem[]> => {
        return this.searchAccounts(params);
    };

    public onBusinesscoChanged(value: string) {
        if (!this.setting) return;

        this.setting.businessco_no = value;

        if (!value?.trim()) return;

        void this.loadGrid();
    }

    public onAccountValueChanged(value: string, field: AccountNameField) {
        if (!this.setting) return;

        this.setting[field] = value;
    }

    public onAccountSelected(item: AccountItem | undefined, field: AccountNameField) {
        if (!this.setting) return;

        if (item) {
            this.setting[this.getAccountNumberField(field)] = item.item_id;
            this.setting[field] = item.item_name;
        } else {
            this.onAccountCleared(field);
        }
    }

    public onAccountCleared(field: AccountNameField) {
        if (!this.setting) return;

        this.setting[this.getAccountNumberField(field)] = '0';
        this.setting[field] = '';
    }

    public async addItem(): Promise<void> {
        if (!this.setting) return;

        const get = this.setting;

        const selectedItems = (this.grid?.gridfunc?.getDirtyRows() ?? []).filter((row) => row['is_selected'] === true || row['is_selected'] === '1');

        if (selectedItems.length === 0) return;

        const progress = this.modal.openProgressDialog({
            rememberId: 'w_custprodaccbcs_grid.progress',
            value: 0,
            max: selectedItems.length,
            label: 'progressbar',
        });

        let count = 0;

        try {
            for (const item of selectedItems) {
                if (progress.data.status === 'cancelled') break;

                const response = await this.custProdAccBcs.saveCustProdAccBcs({
                    ...get,
                    custprodaccbc_keyno: item.custprodaccbc_keyno
                });

                if ('' + response?.errorcode !== '0') {
                    progress.updateStatus('invalid');
                    progress.updateInvalidText(response?.errormessage ?? '');
                    break;
                }

                count++;
                progress.updateValue(count);
            }

            if (count === selectedItems.length) progress.updateStatus('finish');
        } catch (error) {
            progress.updateStatus('invalid');
            progress.updateInvalidText(error instanceof Error ? error.message : '');
        } finally {
            await this.grid?.gridfunc?.read?.();
        }
    }

    private searchAccounts(params?: { value?: string }): Promise<AccountItem[]> {
        return this.typeahead.lookUpSearchAccounts({
            searchtext: params?.value ?? '',
            businessco_no: this.setting?.businessco_no ?? ''
        });
    }

    private async loadEdit() {
        this.setting = await this.custProdAccBcs.getCustProdAccBcs();
    }

    private async loadCompanies() {
        this.selectListCompanies = await this.logistic.getCompanies();
    }

    private async loadGrid() {
        if (!this.setting?.businessco_no?.trim()) return;

        if (!this.grid) {
            this.grid = new Grid<CustProdAccBcsGridRow>()
                .setRememberId('w_custprodaccbcs_grid')
                .setLoadDataFunction({
                    method: 2995,
                    parameters: () => this.setting
                })
                .setKendoConfig({
                    height: '100%',
                    sortable: true,
                    aggregate: true,
                    pager: false,
                    selectable: false,
                    filterable: false
                })
                .setToolbar({ wrapping: true })
                .addToolbarButton({
                    name: 'custprodaccbcs_save_accounts',
                    text: 'custprodaccbcs_save_accounts',
                    func: () => this.addItem(),
                    icon: 'fa-plus',
                    cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r',
                    translate: true,
                    disabled: () => { return this.hasDirty(); }
                });
        } else {
            await this.grid.gridfunc?.read();
        }
    }

    private async hasDirty() {
        let parmItems = this.getIsSelectedItems();

        if (parmItems.length > 0) return false;

        return true;
    }

    private getIsSelectedItems(): any[] {
        const dirtyItems = this.grid?.gridfunc?.getDirtyRows() ?? [];

        return dirtyItems.filter((row) => row['is_selected'] === true || row['is_selected'] === '1');
    }

    private getAccountNumberField(field: AccountNameField): AccountNumberField {
        return field.replace('_name', '_no') as AccountNumberField;
    }

    private async init() {
        await Promise.all([this.loadEdit(), this.loadCompanies()]);
        this.loadGrid();
    }

    ngOnInit(): void {
        void this.init();
    }
}

type AccountNameField = 'account_name' | 'account_name_free' | 'account_name_export' | 'account_name_free_export';

type AccountNumberField = 'account_no' | 'account_no_free' | 'account_no_export' | 'account_no_free_export';
